/** Static copy from Audit Practice Manual (2024 Edition).xlsm — sheets Mi / Ms / M2 / Mapp01 */

import type {G0Page, GProgrammeRow} from '@/pages/components/audit-program/g-section/constants';
import type {ESummaryObjective, ESummaryPage} from '@/pages/components/audit-program/e-section/constants';
import type {SectionIndexRow} from '@/pages/components/audit-program/SectionIndexView';

export type MSectionPageCode = 'Mi' | 'Ms' | 'M2' | 'Mapp01';

export const M_SECTION_PAGES: MSectionPageCode[] = ['Mi', 'Ms', 'M2', 'Mapp01'];

export const M_PAGE_TITLES: Record<MSectionPageCode, string> = {
    Mi: 'Index',
    Ms: 'Summary sheet - Long-term loans and deferred income',
    M2: 'Audit programme - Long-term loans and deferred income',
    Mapp01: 'Supplementary procedures - Long-term loans and deferred income',
};

export type MSectionIndexRow = SectionIndexRow & {
    pageCode?: Exclude<MSectionPageCode, 'Mi'>;
};

export const M_SECTION_INDEX_LETTER = 'M';
export const M_SECTION_INDEX_TITLE = 'LONG-TERM LOANS AND DEFERRED INCOME';

export const M_SECTION_INDEX: MSectionIndexRow[] = [
    {id: 'm-1', kind: 'item', num: '1', title: 'Lead schedule'},
    {id: 'm-2', kind: 'item', num: '2', title: 'Audit programme', pageCode: 'M2'},
    {id: 'm-3', kind: 'item', num: '3', title: 'Confirmation certificates'},
    {id: 'm-s', kind: 'item', num: 's', title: 'Summary sheet', pageCode: 'Ms'},
    {id: 'm-app01', kind: 'item', num: 'app01', title: 'Supplementary procedures', pageCode: 'Mapp01'},
    {id: 'm-4', kind: 'item', num: '4', title: ''},
    {id: 'm-5', kind: 'item', num: '5', title: ''},
    {id: 'm-6', kind: 'item', num: '6', title: ''},
];

export type MProgrammePage = G0Page;
export type MProgrammeRow = GProgrammeRow;
export type MSummaryObjective = ESummaryObjective;
export type MSummaryPage = ESummaryPage;

export const MS_PAGE: MSummaryPage = {
    title: 'SUMMARY SHEET - LONG-TERM LOANS AND DEFERRED INCOME',
    objectivesTitle: 'Audit objectives',
    objectives: [
        {
            id: 'obj1',
            num: '1',
            text: 'To ensure that all material loans have been correctly accounted for.',
            defaultKeyTests: '4, 5, 6',
            defaultSuppRef: '1, 2, 3, 4',
        },
        {
            id: 'obj2',
            num: '2',
            text: 'To ensure that deferred income has been correctly accounted for and is complete.',
            defaultKeyTests: '7',
            defaultSuppRef: '',
        },
        {
            id: 'obj3',
            num: '3',
            text: 'To confirm that all necessary disclosures concerning long-term loans and deferred income have been made and that the information is appropriately presented and described.',
            defaultKeyTests: '8',
            defaultSuppRef: '',
        },
    ],
    planningTitle: 'Planning conclusion',
    finalTitle: 'Final conclusion',
    materialityLabel: 'Materiality (HK$)',
    planningNote:
        'I am satisfied that from the tests planned sufficient appropriate evidence can be gained to satisfy the objectives.',
    finalBullets: [
        'the work has been performed in accordance with the audit programme;',
        'the work performed and the results obtained have been adequately documented;',
        'all necessary information has been collected for the presentation and disclosure in the financial statements;',
        'sufficient appropriate evidence has been obtained to support the audit conclusion reached;',
        'in my opinion (subject to matters highlighted on B6 or B9) long-term loans and deferred income are fairly stated / not fairly stated as described below:',
    ],
};

export const M2_PAGE: MProgrammePage = {
    title: 'AUDIT PROGRAMME – LONG-TERM LOANS AND DEFERRED INCOME',
    rows: [
        {id: 'r1', kind: 'section', num: '', text: 'General', level: 0},
        {id: 'r2', kind: 'main', num: '1', text: 'Obtain and check, or prepare, a lead schedule for the current year\'s figures and reconcile this to the general ledger.', level: 0},
        {id: 'r3', kind: 'main', num: '2', text: 'Carry out analytical procedures such as:', level: 0},
        {id: 'r4', kind: 'sub', num: '(a)', text: 'comparison of the current figures with those of prior periods;', level: 1},
        {id: 'r5', kind: 'sub', num: '(b)', text: 'review and comparison of key ratios or other performance indicators.', level: 1},
        {id: 'r6', kind: 'main', num: '3', text: 'Review for items above performance materiality or that are otherwise unusual and verify.', level: 0},
        {id: 'r7', kind: 'section', num: '', text: 'Loans', level: 0},
        {id: 'r8', kind: 'main', num: '4', text: 'Obtain direct confirmation from lenders and confirm that other matters such as security have been dealt with.', level: 0},
        {id: 'r9', kind: 'main', num: '5', text: 'Review loan agreements and consider the implications of any breach of covenants.', level: 0},
        {id: 'r10', kind: 'main', num: '6', text: 'Calculate the apportionment of loan between non-current and current portion.', level: 0},
        {id: 'r11', kind: 'section', num: '', text: 'Deferred income', level: 0},
        {id: 'r12', kind: 'main', num: '7', text: 'Review the basis for deferring income', level: 0},
        {id: 'r13', kind: 'sub', num: '(a)', text: 'vouch to supporting documents; and', level: 1},
        {id: 'r14', kind: 'sub', num: '(b)', text: 'ensure that it is valid, complete and has been correctly and consistently applied with the prior year and applicable accounting standards.', level: 1},
        {id: 'r15', kind: 'section', num: '', text: 'Presentation and disclosure', level: 0},
        {id: 'r16', kind: 'main', num: '8', text: 'Ensure that there is sufficient appropriate evidence on the file to support all disclosures made.', level: 0},
        {id: 'r17', kind: 'section', num: '', text: 'Bespoke tests', level: 0},
        {id: 'r18', kind: 'main', num: '9', text: 'Draft and perform any necessary additional tests such as those required by C8.3 and cross reference with the objectives on the summary sheet.', level: 0},
        {id: 'r19', kind: 'section', num: '', text: 'Conclusion', level: 0},
        {id: 'r20', kind: 'main', num: '10', text: 'Consider whether there are any points which need to be included in a letter of representation or letter of comment and record on A5 or A6 as appropriate.', level: 0},
        {id: 'r21', kind: 'main', num: '11', text: 'Review the planned extent of reliance on internal controls in this area and consider whether this remains appropriate.', level: 0},
        {id: 'r22', kind: 'main', num: '12', text: 'Assess whether the initial materiality and/or risk assessment should be revised in view of the audit evidence obtained.  Record details of any necessary adjustments on B6 or B9.  Consider the impact on the remainder of the audit work and on any work undertaken to date.', level: 0},
    ],
};

export const MAPP01_PAGE: MProgrammePage = {
    title: 'SUPPLEMENTARY PROCEDURES - LONG-TERM LOANS AND DEFERRED INCOME',
    rows: [
        {id: 'r1', kind: 'main', num: '1', text: 'For long-term loans,', level: 0},
        {id: 'r2', kind: 'sub', num: '(a)', text: 'Check supporting evidence for loan advance and repayment during the year.', level: 1},
        {id: 'r3', kind: 'sub', num: '(b)', text: 'Check memorandum of satisfaction for loan relating to charges previously filed with Company Registry.', level: 1},
        {id: 'r4', kind: 'sub', num: '(c)', text: 'Determine split of loans between secured/unsecured and interest bearing/non-interest bearing.', level: 1},
        {id: 'r5', kind: 'sub', num: '(d)', text: 'Verify interest charged for the period and the adequacy of accrued interest.', level: 1},
        {id: 'r6', kind: 'sub', num: '(e)', text: 'Confirm that loans payable more than one year from the balance sheet date are measured in accordance with applicable accounting standards.', level: 1},
        {id: 'r7', kind: 'main', num: '2', text: 'Ensure the completeness of loans by:', level: 0},
        {id: 'r8', kind: 'sub', num: '(a)', text: 'examining board minutes (Ref. Section O);', level: 1},
        {id: 'r9', kind: 'sub', num: '(b)', text: 'reviewing bank confirmation and other correspondences (Ref. Section K);', level: 1},
        {id: 'r10', kind: 'sub', num: '(c)', text: 'performing search for unrecorded liabilities (Ref. Section T).', level: 1},
        {id: 'r11', kind: 'main', num: '3', text: 'Ensure the entity accounted for foreign currency transactions in accordance with applicable accounting standards.', level: 0},
        {id: 'r12', kind: 'main', num: '4', text: 'Confirm that all monetary liabilities have been translated at appropriate closing rate.', level: 0},
    ],
};
